/**
 * Bridge between the HTTP layer and whatever signs the user in (MSAL once auth is ported).
 * It has no React or module dependency: the auth module registers a provider once at bootstrap,
 * the request interceptor asks it for a token, and the app listens to `authEvents`.
 */

export interface TokenRequestOptions {
  /** Skip the cache and get a new token from the identity provider (used after a 401). */
  forceRefresh?: boolean;
}

/** Resolves to an access token, or `null` when there is no signed-in user. */
export type AccessTokenProvider = (options?: TokenRequestOptions) => Promise<string | null>;

let provider: AccessTokenProvider = () => Promise.resolve(null);

/** Register the token source. Call once at bootstrap (`main.tsx`), never inside a render or effect. */
export const setAccessTokenProvider = (next: AccessTokenProvider) => {
  provider = next;
};

export const getAccessToken = (options?: TokenRequestOptions): Promise<string | null> => {
  return provider(options);
};

/**
 * - `session-expired`: a request is still 401 after a forced refresh. Sign the user out.
 * - `interaction-required`: the identity provider needs the user (MFA, consent, expired refresh
 *   token). Prompt them to sign in again; never open a popup from inside a request.
 */
export type AuthEvent = 'session-expired' | 'interaction-required';

const listeners = new Map<AuthEvent, Set<() => void>>();

export const authEvents = {
  /** Subscribe; returns the unsubscribe function (fits `useEffect` cleanup). */
  on: (event: AuthEvent, listener: () => void): (() => void) => {
    const set = listeners.get(event) ?? new Set();
    set.add(listener);
    listeners.set(event, set);
    return () => set.delete(listener);
  },
  emit: (event: AuthEvent) => {
    listeners.get(event)?.forEach((listener) => listener());
  },
};
